import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Bell, Settings, CheckCheck, UserPlus, Trophy, Sparkles, Hand } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import React from "react";
import { PreviewBanner } from "@/components/PreviewBanner";

// Fabricated data, presented only as illustrative content — not the user's
// real notifications. See apps/smartpoker/src/components/PreviewBanner.tsx
// for the MOCK_ naming + <PreviewBanner /> convention this pairs with.
const MOCK_NOTIFICATIONS = [
    {
        id: 1,
        icon: UserPlus,
        iconClass: "text-accent",
        titleKey: "notifications_friend_request_title",
        descKey: "notifications_friend_request_desc",
        descParams: { name: "AceHigh92" },
        time: "2h ago",
        read: false,
    },
    {
        id: 2,
        icon: Trophy,
        iconClass: "text-secondary",
        titleKey: "notifications_unlocked_title",
        descKey: "notifications_unlocked_desc",
        time: "1d ago",
        read: false,
    },
    {
        id: 3,
        icon: Sparkles,
        iconClass: "text-primary",
        titleKey: "notifications_new_feature_title",
        descKey: "notifications_new_feature_desc",
        time: "3d ago",
        read: true,
    },
    {
        id: 4,
        icon: Hand,
        iconClass: "text-destructive",
        titleKey: "notifications_welcome_title",
        descKey: "notifications_welcome_desc",
        time: "1w ago",
        read: true,
    },
];

// --- Notifications Page Component ---
const Notifications = () => {
    const { t } = useTranslation();
    const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

    const markAllAsRead = () => {
        setNotifications(notifications.map(n => ({ ...n, read: true })));
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="min-h-screen bg-background pb-24">
            <div className="p-4 space-y-6 max-w-screen-xl mx-auto">
                {/* Header */}
                <header className="flex items-center gap-4">
                    <Button asChild variant="ghost" size="icon" className="shrink-0">
                        <Link to="/profile">
                            <ArrowLeft className="w-6 h-6" />
                        </Link>
                    </Button>
                    <div className="flex items-center gap-3">
                        <div className="bg-primary/10 p-3 rounded-xl text-primary">
                            <Bell className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t('notifications_title')}</h1>
                            <p className="text-sm text-muted-foreground">{t('notifications_subtitle')}</p>
                        </div>
                    </div>
                </header>

                <PreviewBanner
                    titleKey="notifications_preview_title"
                    descriptionKey="notifications_preview_desc"
                />

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                    <Button onClick={markAllAsRead} disabled={unreadCount === 0} className="flex-1">
                        <CheckCheck className="w-4 h-4 mr-2" />
                        {t('notifications_mark_all_read')}
                    </Button>
                    <Button variant="outline" size="icon" asChild>
                         <Link to="/settings">
                            <Settings className="w-5 h-5" />
                        </Link>
                    </Button>
                </div>

                {/* Notifications List */}
                <Card className="p-0">
                    {notifications.length > 0 ? (
                        <div className="divide-y">
                            {notifications.map(notification => {
                                const Icon = notification.icon;
                                return (
                                    <div
                                        key={notification.id}
                                        className={cn(
                                            "p-4 flex items-start gap-4 transition-colors",
                                            !notification.read && "bg-muted/50"
                                        )}
                                    >
                                        <div className={cn("mt-1", notification.iconClass)}>
                                           <Icon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold">{t(notification.titleKey)}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {t(notification.descKey, notification.descParams)}
                                            </p>
                                        </div>
                                        {!notification.read && (
                                            <div className="w-2.5 h-2.5 bg-accent rounded-full mt-1.5 shrink-0"></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground py-16">{t('notifications_no_notifications')}</p>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default Notifications;
